/**
 * Off-canvas drawer — the navbar collapses to this on mobile. Slides in from a
 * side, traps focus, closes on Escape or scrim click, restores focus on unmount.
 * Token-driven, reduced-motion honored.
 */
import type { JSX } from 'solid-js';
import { onCleanup, Show } from 'solid-js';
import { Portal } from 'solid-js/web';
import { trapFocus } from '../focus/trap-focus.ts';
import { ensureBaseStyles } from '../internal/base-styles.ts';
import { cx } from '../internal/cx.ts';
import { ensureStyles } from '../internal/inject-styles.ts';

export type DrawerSide = 'left' | 'right';

const STYLE_ID = 'f4-drawer';
const CSS = `
.f4-drawer-scrim {
  position: fixed;
  inset: 0;
  display: flex;
  z-index: var(--z-drawer, 200);
}
.f4-drawer-scrim--right { justify-content: flex-end; }
.f4-drawer-scrim__backdrop {
  position: absolute;
  inset: 0;
  border: none;
  padding: 0;
  background: var(--color-scrim);
  cursor: pointer;
  animation: f4-fade-in var(--duration-fast, 150ms) var(--ease-standard, ease);
}
.f4-drawer {
  position: relative;
  width: min(20rem, 85vw);
  height: 100%;
  overflow: auto;
  padding: var(--space-5);
  background: var(--color-surface-overlay);
  color: var(--color-text);
  box-shadow: var(--elevation-4);
}
/* The ring sits inside the panel edge, so it stays visible against the scrim. */
.f4-drawer { --f4-focus-offset: -2px; }
.f4-drawer--left { animation: f4-slide-in-left var(--duration-base, 240ms) var(--ease-decelerate, ease); }
.f4-drawer--right { animation: f4-slide-in-right var(--duration-base, 240ms) var(--ease-decelerate, ease); }
@media (prefers-reduced-motion: reduce) {
  .f4-drawer-scrim, .f4-drawer { animation: none; }
}
`;

export interface DrawerProps {
  /** Whether the drawer is shown. */
  readonly open: boolean;
  /** Requested close (Escape, scrim click). */
  readonly onClose: () => void;
  /** Which edge it anchors to. Defaults to `left`. */
  readonly side?: DrawerSide;
  /** id of the element labelling the drawer. */
  readonly labelledBy?: string;
  /** Accessible label for the backdrop close button (no bare literal). */
  readonly closeLabel: string;
  readonly class?: string;
  readonly children?: JSX.Element;
}

export function Drawer(props: DrawerProps): JSX.Element {
  ensureBaseStyles();
  ensureStyles(STYLE_ID, CSS);

  let dispose: (() => void) | undefined;
  const attach = (node: HTMLElement): void => {
    dispose = trapFocus(node, props.onClose);
  };
  onCleanup(() => dispose?.());

  const side = (): DrawerSide => props.side ?? 'left';

  return (
    <Show when={props.open}>
      <Portal>
        <div class={cx('f4-drawer-scrim', `f4-drawer-scrim--${side()}`)}>
          <button
            type="button"
            class="f4-drawer-scrim__backdrop"
            aria-label={props.closeLabel}
            onClick={() => props.onClose()}
          />
          <aside
            ref={attach}
            role="dialog"
            aria-modal="true"
            aria-labelledby={props.labelledBy}
            tabindex="-1"
            class={cx('f4-drawer', `f4-drawer--${side()}`, 'f4-focus-ring', props.class)}
          >
            {props.children}
          </aside>
        </div>
      </Portal>
    </Show>
  );
}
